import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, TRADE_OPTIONS } from "@/lib/api";

/**
 * Quick edit of the "header" fields of a devis when the AI got it wrong:
 *   - Contractor name (often a random word picked up from the PDF)
 *   - Trade / corps de métier (often misclassified)
 *   - Reference (n° devis)
 *   - Email
 */
export default function EditDevisHeaderDialog({ devis, onSaved, trigger }) {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        contractor_name: devis.contractor_name || "",
        trade: devis.trade || "non_classe",
        reference: devis.reference || "",
        contractor_email: devis.contractor_email || "",
        contractor_phone: devis.contractor_phone || "",
    });

    useEffect(() => {
        if (open) {
            setForm({
                contractor_name: devis.contractor_name || "",
                trade: devis.trade || "non_classe",
                reference: devis.reference || "",
                contractor_email: devis.contractor_email || "",
                contractor_phone: devis.contractor_phone || "",
            });
        }
    }, [open, devis]);

    const submit = async () => {
        if (!form.contractor_name.trim()) {
            toast.error("Le nom de l'entreprise est obligatoire");
            return;
        }
        setSaving(true);
        try {
            await api.updateDevis(devis.id, {
                contractor_name: form.contractor_name.trim(),
                trade: form.trade,
                reference: form.reference || null,
                contractor_email: form.contractor_email || null,
                contractor_phone: form.contractor_phone || null,
            });
            toast.success("Devis corrigé");
            setOpen(false);
            onSaved && onSaved();
        } catch {
            toast.error("Erreur d'enregistrement");
        } finally {
            setSaving(false);
        }
    };

    const isInList = TRADE_OPTIONS.find((t) => t.value === form.trade);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {trigger || (
                    <button
                        className="p-2 text-ink-soft hover:text-accent transition-colors"
                        title="Corriger le nom / corps de métier"
                        data-testid={`edit-devis-header-${devis.id}`}
                    >
                        <Pencil className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                )}
            </DialogTrigger>
            <DialogContent className="rounded-none border-ink max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">
                        Corriger le devis
                    </DialogTitle>
                </DialogHeader>
                <div className="text-xs text-ink-soft border-l-2 border-accent pl-3">
                    Modifiez le nom et le corps de métier si l'IA s'est
                    trompée. Le tableau budget et la comparaison seront
                    automatiquement recalculés.
                </div>

                <div className="space-y-4">
                    <div>
                        <Label className="stat-label">
                            Entreprise / Artisan *
                        </Label>
                        <Input
                            data-testid="edit-contractor-name"
                            className="rounded-none mt-1.5"
                            value={form.contractor_name}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    contractor_name: e.target.value,
                                })
                            }
                        />
                    </div>
                    <div>
                        <Label className="stat-label">Corps de métier</Label>
                        <Select
                            value={isInList ? form.trade : "non_classe"}
                            onValueChange={(v) =>
                                setForm({ ...form, trade: v })
                            }
                        >
                            <SelectTrigger
                                data-testid="edit-trade"
                                className="rounded-none mt-1.5"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="rounded-none max-h-[280px]">
                                {TRADE_OPTIONS.map((opt) => (
                                    <SelectItem
                                        key={opt.value}
                                        value={opt.value}
                                    >
                                        {opt.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {!isInList && (
                            <div className="text-[10px] text-warn mt-1">
                                Le devis avait un libellé non standard
                                ("{devis.trade}") — il a été reclassé sur
                                "Non classé". Choisissez le bon corps de
                                métier dans la liste.
                            </div>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="stat-label">N° devis</Label>
                            <Input
                                className="rounded-none mt-1.5"
                                value={form.reference}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        reference: e.target.value,
                                    })
                                }
                            />
                        </div>
                        <div>
                            <Label className="stat-label">Email contact</Label>
                            <Input
                                className="rounded-none mt-1.5"
                                value={form.contractor_email}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        contractor_email: e.target.value,
                                    })
                                }
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="stat-label">Téléphone</Label>
                        <Input
                            className="rounded-none mt-1.5"
                            value={form.contractor_phone}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    contractor_phone: e.target.value,
                                })
                            }
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        className="rounded-none"
                        onClick={() => setOpen(false)}
                        disabled={saving}
                    >
                        Annuler
                    </Button>
                    <Button
                        data-testid="edit-devis-submit"
                        className="rounded-none bg-ink text-white hover:bg-ink/90"
                        onClick={submit}
                        disabled={saving}
                    >
                        {saving ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Enregistrement…
                            </>
                        ) : (
                            "Enregistrer"
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
